/**
 * SET PROFILE FIRST panel v2 (2026-05-05 #9)
 *
 * Audit-only UI for the v2 archetype engine. Renders separately:
 *   - PROFILE: archetype + confidence
 *   - CONTROL: control_score, strength_gap, short_win_skew
 *   - LENGTH: extended_match, five_set_total, drag_risk
 *   - VOLATILITY: volatility_score
 *   - SIDE CONTROL: favorite_side + fav/dog short_win + fav 3-0/3-1/3-2
 *   - MARKET SCORES + RANKED MARKETS
 *
 * Per-archetype contextual messages explain CONTROLLED_LONG / BALANCED_LONG /
 * TRUE_CHAOS scenarios for the operator.
 */
import React, { useEffect, useState } from 'react';
import { adminGet } from '../data/api';
import { Activity, RefreshCcw, Layers, Target } from 'lucide-react';

const PROFILE_COLORS = {
  STOMP_CLEAN:     { border: 'border-violet-700', bg: 'bg-violet-950/40', text: 'text-violet-200' },
  CONTROLLED_FAST: { border: 'border-emerald-700', bg: 'bg-emerald-950/40', text: 'text-emerald-200' },
  CONTROLLED_LONG: { border: 'border-cyan-700', bg: 'bg-cyan-950/40', text: 'text-cyan-200' },
  BALANCED_LONG:   { border: 'border-amber-700', bg: 'bg-amber-950/40', text: 'text-amber-200' },
  TRUE_CHAOS:      { border: 'border-rose-700', bg: 'bg-rose-950/40', text: 'text-rose-200' },
  BALANCED:        { border: 'border-zinc-700', bg: 'bg-zinc-900/60', text: 'text-zinc-200' },
};

const MARKET_LABEL = {
  mw: 'MW', hc_fav: 'HC Favorite', hc_dog: 'HC Underdog',
  over: 'Over', under: 'Under',
};

const fmtPct = (v, d = 1) => (v == null ? '—' : `${(Number(v) * 100).toFixed(d)}%`);
const fmtScore = (v, d = 2) => (v == null ? '—' : Number(v).toFixed(d));
const fmtEv = (v) =>
  v == null ? '—' : `${v >= 0 ? '+' : ''}${Number(v).toFixed(1)}%`;

export default function SetProfilePanel({ matchId, pin, autoLoad = true }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  const load = async () => {
    if (!matchId) return;
    setLoading(true);
    setErr(null);
    try {
      const r = await adminGet(
        `/tt/admin/set-profile-analysis?match_id=${encodeURIComponent(matchId)}&persist=true`,
        pin,
      );
      setData(r);
    } catch (e) {
      setErr(e?.message || 'analysis failed');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (autoLoad && matchId) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matchId, autoLoad]);

  if (!matchId) return null;
  const c = PROFILE_COLORS[data?.profile_type] || PROFILE_COLORS.BALANCED;
  const profile = data?.profile || {};
  const ms = data?.market_scores || {};
  const ranked = data?.ranked_markets || [];

  return (
    <div
      data-testid="set-profile-panel"
      className={`rounded-sm border ${c.border} ${c.bg} p-3 space-y-3 text-[11px]`}
      style={{ fontFamily: 'monospace' }}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Layers size={14} className={c.text} />
          <span className="text-zinc-100 font-semibold tracking-wide">
            SET PROFILE FIRST
          </span>
          <span className="text-[9px] text-zinc-500">
            v2 · audit-only · {data?.config_version || ''}
          </span>
        </div>
        <button
          data-testid="set-profile-reload-btn"
          onClick={load}
          disabled={loading}
          className="flex items-center gap-1 px-2 py-1 rounded-sm bg-zinc-800/60 border border-zinc-700 text-zinc-300 hover:text-zinc-100 disabled:opacity-50"
        >
          <RefreshCcw size={11} className={loading ? 'animate-spin' : ''} />
          {loading ? 'loading' : 'reload'}
        </button>
      </div>

      {err && (
        <div className="text-rose-300 px-2 py-1 rounded-sm bg-rose-950/40 border border-rose-800">
          {err}
        </div>
      )}

      {data && (
        <>
          {/* Contextual message */}
          {data.conflict_reason && (
            <div
              data-testid="set-profile-context-msg"
              className={`px-2 py-1.5 rounded-sm border ${c.border} ${c.bg} ${c.text} text-[10.5px] leading-snug`}
            >
              {data.conflict_reason}
            </div>
          )}

          {/* PROFILE row */}
          <div className="flex flex-wrap items-center gap-2">
            <div className="text-[10px] text-zinc-500 tracking-wider">PROFILE</div>
            <span
              data-testid="set-profile-type"
              className={`px-2 py-0.5 rounded-sm font-bold ${c.text} ${c.bg} border ${c.border}`}
            >
              {data.profile_type}
            </span>
            <span className="text-zinc-500">·</span>
            <span className="text-zinc-400 text-[10px]">confidence</span>
            <span
              data-testid="set-profile-confidence"
              className="text-zinc-100 font-semibold"
            >
              {fmtPct(data.profile_confidence, 0)}
            </span>
            {data.low_confidence_active && (
              <span
                data-testid="set-profile-low-confidence-tag"
                className="text-[9px] px-1.5 py-0.5 rounded-sm bg-amber-900/40 border border-amber-700 text-amber-200"
              >
                LOW-CONFIDENCE
              </span>
            )}
            <span className="ml-auto text-[9px] text-zinc-500">
              fatigue {data.fatigue_type}
            </span>
          </div>

          {/* CONTROL / LENGTH / VOLATILITY */}
          <div className="grid grid-cols-3 gap-2">
            <ScoreBlock
              label="CONTROL"
              testid="score-control"
              main={fmtScore(data.control_score, 2)}
              extras={[
                ['gap', fmtPct(data.strength_gap_primary, 0)],
                ['skew', fmtPct(profile.short_win_skew, 0)],
              ]}
            />
            <ScoreBlock
              label="LENGTH"
              testid="score-length"
              main={fmtScore(data.length_score, 2)}
              extras={[
                ['ext', fmtPct(profile.extended_match, 0)],
                ['5-set', fmtPct(data.five_set_total, 0)],
              ]}
            />
            <ScoreBlock
              label="VOLATILITY"
              testid="score-volatility"
              main={fmtScore(data.volatility_score, 2)}
              extras={[
                ['drag', fmtPct(profile.drag_risk, 0)],
              ]}
            />
          </div>

          {/* SIDE CONTROL */}
          <div className="rounded-sm bg-zinc-900/40 border border-zinc-800 p-2"
               data-testid="side-control">
            <div className="text-[10px] text-zinc-500 mb-1 tracking-wider">
              SIDE CONTROL · favorite{' '}
              <b className="text-zinc-200">{profile.favorite_side}</b>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 text-[10px]">
              <Cell label="fav 3-0" value={fmtPct(profile.fav_3_0, 0)} highlight />
              <Cell label="fav 3-1" value={fmtPct(profile.fav_3_1, 0)} />
              <Cell label="fav 3-2" value={fmtPct(profile.fav_3_2, 0)} />
              <Cell
                label="fav short_win"
                value={fmtPct(data.fav_short_win, 0)}
                highlight
              />
              <Cell
                label="dog short_win"
                value={fmtPct(data.dog_short_win, 0)}
              />
            </div>
          </div>

          {/* MARKET SCORES + RANKED */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-1 border-t border-zinc-800">
            <div>
              <div className="text-[10px] text-zinc-500 mb-1 tracking-wider">
                MARKET SCORES (per archetype)
              </div>
              <table className="w-full text-[10px]">
                <tbody>
                  {Object.entries(ms).map(([k, v]) => (
                    <tr key={k} className="text-zinc-300">
                      <td className="py-0.5">{MARKET_LABEL[k] || k}</td>
                      <td className="py-0.5">
                        <div className="flex items-center gap-1">
                          <div className="w-20 h-1.5 bg-zinc-800 rounded-sm overflow-hidden">
                            <div
                              className={`h-full ${c.text.replace('text', 'bg')}`}
                              style={{ width: `${(v * 100).toFixed(0)}%` }}
                            />
                          </div>
                          <span className="text-zinc-100 ml-1">{fmtScore(v)}</span>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div>
              <div className="text-[10px] text-zinc-500 mb-1 tracking-wider">
                RANKED MARKETS · final = score × confidence × ev_quality
              </div>
              <table className="w-full text-[10px]">
                <thead>
                  <tr className="text-zinc-500">
                    <th className="text-left py-0.5">market</th>
                    <th className="text-right">EV</th>
                    <th className="text-right">ev_q</th>
                    <th className="text-right">final</th>
                  </tr>
                </thead>
                <tbody>
                  {ranked.map((r, i) => (
                    <tr
                      key={r.market}
                      data-testid={`set-profile-ranked-${i}`}
                      className={
                        r.market === data.recommended_market
                          ? 'text-emerald-300 font-semibold'
                          : !r.eligible
                          ? 'text-zinc-600 line-through'
                          : 'text-zinc-300'
                      }
                      title={r.blocked_reason || ''}
                    >
                      <td className="py-0.5">
                        {MARKET_LABEL[r.market] || r.market}
                      </td>
                      <td className={`text-right ${
                        r.ev_pct == null ? 'text-zinc-500'
                        : r.ev_pct > 0 ? 'text-emerald-300' : 'text-rose-300'
                      }`}>
                        {fmtEv(r.ev_pct)}
                      </td>
                      <td className="text-right text-zinc-400">
                        {fmtScore(r.ev_quality, 2)}
                      </td>
                      <td className="text-right">
                        {fmtScore(r.final_score, 3)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Recommendation */}
          <div className="pt-2 border-t border-zinc-800 flex items-center gap-2 flex-wrap">
            <Target size={12} className="text-emerald-400" />
            <span className="text-[10px] text-zinc-500">RECOMMENDED MARKET</span>
            <span
              data-testid="set-profile-recommended"
              className="text-emerald-200 font-bold tracking-wider"
            >
              {data.recommended_market
                ? (MARKET_LABEL[data.recommended_market] || data.recommended_market).toUpperCase()
                : 'NONE (no eligible market with positive EV)'}
            </span>
            {data.recommended_score != null && (
              <span className="text-zinc-500 text-[10px]">
                score {fmtScore(data.recommended_score, 3)}
              </span>
            )}
          </div>

          <div className="text-[9px] text-zinc-600">
            decision_path: <code className="text-zinc-400">{data.decision_path}</code>
            {' · '}audit-only · zero LLM · zero auto-bet
          </div>
        </>
      )}
    </div>
  );
}

function ScoreBlock({ label, main, extras, testid }) {
  return (
    <div
      data-testid={testid}
      className="rounded-sm bg-zinc-900/60 border border-zinc-800 p-2"
    >
      <div className="text-[9px] text-zinc-500 tracking-wider">{label}</div>
      <div className="text-zinc-100 text-lg font-bold leading-tight">{main}</div>
      {extras?.map(([k, v]) => (
        <div key={k} className="text-[9px] text-zinc-500">
          {k} <b className="text-zinc-300">{v}</b>
        </div>
      ))}
    </div>
  );
}

function Cell({ label, value, highlight }) {
  return (
    <div className={`rounded-sm p-1 ${highlight ? 'bg-zinc-800/50' : ''}`}>
      <div className="text-[9px] text-zinc-500">{label}</div>
      <div className="text-zinc-100 font-semibold">{value}</div>
    </div>
  );
}
